import React, { useState } from "react";
import Card from "src/components/Card/Card";
import TextInput from "src/components/TextInput/TextInput";
import TextArea from "src/components/TextArea/TextArea";
import Button from "src/components/Button/Button";
import Axios from "src/services/api";
import { toast } from "react-hot-toast";
import styles from "./Contact.module.scss";
import { FormGroup } from "src/components";
import { PageTitle } from "src/components/PageTitle";
import { Check } from "react-feather";
import { useNavigate } from "react-router-dom";

const INQUIRY_TOPICS = [
  "Account",
  "Products",
  "Checkout",
  "Transactions",
  "Reviews",
  "Discounts",
  "Upsells",
  "Payouts",
  "Settings",
  "Taxes",
  "Connecting bank account",
  "Connecting PayPal",
  "Account approval",
  "Webhooks",
  "Refunds",
  "Other",
];

interface ContactFormResponse {
  message: string;
  ticketId: string;
  threaded: boolean;
  originalTicketId?: string;
}

const Contact = () => {
  const [formData, setFormData] = useState({
    email: "",
    topic: "",
    message: "",
    ticketId: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResponse, setSubmitResponse] = useState<ContactFormResponse | null>(null);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.topic || !formData.message) {
      toast.error("Please fill out all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const cleanedFormData = {
        ...formData,
        ticketId: formData.ticketId.replace(/^#/, '').trim()
      };

      const response = await Axios.post("/users/contact", cleanedFormData);

      setSubmitResponse(response.data);

      const message = response.data.threaded
        ? `Message added to existing ticket thread (Original: ${response.data.originalTicketId}, New: ${response.data.ticketId})`
        : `New ticket created: ${response.data.ticketId}`;

      setFormData({
        email: "",
        topic: "",
        message: "",
        ticketId: "",
      });
      setSubmitted(true);
    } catch (error) {
      toast.error("Failed to send message. Please try again later.");
      console.error("Contact form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderThankYouMessage = () => {
    return (
      <div className={styles.thankYouContainer}>
        <div className={styles.checkContainer}>
          <Check className={styles.checkIcon} size={44} />
        </div>
        <h2 className={styles.thankYouTitle}>Thank you for your message!</h2>
        <p className={styles.thankYouText}>
          We've received your inquiry and will get back to you via email as soon as possible.
        </p>
        {submitResponse && (
          <div className={styles.ticketInfo}>
            <p className={styles.ticketDetails}>
              {submitResponse.threaded ? (
                <>
                  <strong>Ticket ID:</strong> {submitResponse.ticketId}<br />
                  <span className={styles.threadingNote}>
                    This message was added to your existing conversation thread.
                  </span>
                </>
              ) : (
                <>
                  <strong>New Ticket ID:</strong> {submitResponse.ticketId}<br />
                  <span className={styles.threadingNote}>
                    Save this ticket ID to continue this conversation in future messages.
                  </span>
                </>
              )}
            </p>
          </div>
        )}
        <div className={styles.thankYouButtons}>
          <Button
            text="Go to Dashboard"
            onClick={() => navigate('/')}
          />
          <Button
            text="Send another message"
            onClick={() => {
              setSubmitted(false);
              setSubmitResponse(null);
            }}
            variant="secondary"
          />
        </div>
      </div>
    );
  };

  const renderContactForm = () => {
    return (
      <form onSubmit={handleSubmit} className={styles.contactForm}>
        <FormGroup label="Contact email" required>
          <TextInput
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Your email address"
          />
        </FormGroup>

        <FormGroup label="Topic" required>
          <select
            name="topic"
            value={formData.topic}
            onChange={handleChange}
            className={styles.topicSelect}
          >
            <option value="">Select a topic</option>
            {INQUIRY_TOPICS.map((topic) => (
              <option key={topic} value={topic}>
                {topic}
              </option>
            ))}
          </select>
        </FormGroup>

        <FormGroup label="Existing Ticket ID (Optional)">
          <TextInput
            name="ticketId"
            type="text"
            value={formData.ticketId}
            onChange={handleChange}
            placeholder="Leave empty if new ticket"
          />
          <p className={styles.helpText}>
            Enter an existing ticket ID to continue that conversation thread. Leave empty to create a new ticket.
          </p>
        </FormGroup>

        <FormGroup label="Message" required>
          <TextArea
            name="message"
            value={formData.message}
            onChange={handleTextAreaChange}
            placeholder="How can we help you?"
            rows={6}
          />
        </FormGroup>

        <Button
          text={isSubmitting ? "Sending..." : "Send Message"}
          type="submit"
          disabled={isSubmitting}
          loading={isSubmitting}
        />
      </form>
    );
  };

  return (
    <div className={styles.pageContainer}>
      <PageTitle>Contact Us</PageTitle>

      <div className={styles.contactPage}>
        <Card>
          {submitted ? renderThankYouMessage() : (
            <>
              <p className={styles.subtitle}>
                Have questions or need assistance? We're here to help!
              </p>
              {renderContactForm()}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Contact; 